import {
  JobStatus,
  PartImageCandidate,
  PartImageEvidenceTier,
  PartImageIdentification,
  PartImageIdentificationOutcome,
  PartImageIdentificationStage,
} from '@/api/generatedApi';
import { FadeIn } from '@/components/PartFinder/FadeIn';
import { ImageMatchConfidencePill } from '@/components/PartFinder/confidence/ConfidencePill';
import { Alert, Box, Button, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { LabelEvidence, PendingLabelCard } from './LabelEvidence';
import { readTextLabel } from './partImageReadText';

const STAGGER_MS = 60;
const MAX_STAGGERED_ROWS = 8;
const SKELETON_CHIP_WIDTHS = [112, 96, 136, 88];
const SKELETON_CANDIDATE_ROWS = 3;

const CandidateRow = styled(FadeIn)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '16px',
  padding: '12px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
}));

const Panes = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gap: '24px',
  [theme.breakpoints.up('md')]: {
    flex: 1,
    minHeight: 0,
    gridTemplateColumns: '380px minmax(0, 1fr)',
    gridTemplateRows: 'minmax(0, 1fr)',
  },
}));

const Pane = styled(Box)(({ theme }) => ({
  minWidth: 0,
  [theme.breakpoints.up('md')]: { overflowY: 'auto', paddingRight: '4px' },
}));

const EMPTY_MESSAGES: Partial<Record<PartImageIdentificationOutcome, string>> = {
  [PartImageIdentificationOutcome.NoPart]: 'No part was visible in these photos. Try a closer shot of the part.',
  [PartImageIdentificationOutcome.NoCandidates]:
    'No FLIS match was found. Try a photo that shows a label, nameplate, or stamped part number.',
};

const staggered = (index: number) => ({ animationDelay: `${Math.min(index, MAX_STAGGERED_ROWS) * STAGGER_MS}ms` });

const isComplete = (identification?: PartImageIdentification): identification is PartImageIdentification =>
  identification?.status === JobStatus.COMPLETED;

const photosRead = (identification?: PartImageIdentification): identification is PartImageIdentification =>
  identification !== undefined && identification.stage !== PartImageIdentificationStage.ReadingPhotos;

const onlySameTypeParts = (candidates: PartImageCandidate[]): boolean =>
  candidates.length > 0 && candidates.every(candidate => candidate.evidenceTier === PartImageEvidenceTier.Inferred);

const SectionHeading = ({ children, testId }: { children: string; testId?: string }) => (
  <Typography variant='subtitle2' data-testid={testId}>
    {children}
  </Typography>
);

const ReadText = ({ identification }: { identification: PartImageIdentification }) => {
  if (identification.readText.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary'>
        No identifiers could be read from the photos.
      </Typography>
    );
  }
  return (
    <Stack direction='row' gap='8px' flexWrap='wrap' alignItems='center'>
      <Typography variant='body2' color='text.secondary'>
        Read from photos:
      </Typography>
      {identification.readText.map((text, index) => (
        <FadeIn key={`${text.kind}:${text.text}`} style={staggered(index)}>
          <Chip size='small' label={readTextLabel(text)} />
        </FadeIn>
      ))}
    </Stack>
  );
};

const SummarySkeleton = () => (
  <Stack gap='8px' data-testid='part-image-summary-pending'>
    <Skeleton variant='text' width='92%' />
    <Skeleton variant='text' width='64%' />
    <Stack direction='row' gap='8px' alignItems='center'>
      <Typography variant='body2' color='text.secondary'>
        Read from photos:
      </Typography>
      {SKELETON_CHIP_WIDTHS.map(width => (
        <Skeleton key={width} variant='rounded' width={width} height={24} sx={{ borderRadius: '12px' }} />
      ))}
    </Stack>
  </Stack>
);

const PhotoSummary = ({ identification }: { identification?: PartImageIdentification }) =>
  photosRead(identification) ? (
    <FadeIn key='summary' sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {identification.description && <Typography variant='body2'>{identification.description}</Typography>}
      <ReadText identification={identification} />
    </FadeIn>
  ) : (
    <FadeIn key='summary-pending'>
      <SummarySkeleton />
    </FadeIn>
  );

const OutcomeMessages = ({ identification }: { identification: PartImageIdentification }) => {
  const emptyMessage = identification.outcome && EMPTY_MESSAGES[identification.outcome];
  const reportCreated = identification.outcome === PartImageIdentificationOutcome.ReportCreated;
  return (
    <>
      {emptyMessage && <Typography variant='body2'>{emptyMessage}</Typography>}
      {reportCreated && identification.reportId && (
        <Alert severity='success' data-testid='part-image-report-created'>
          Identified from the photos. A report was created for {identification.candidates[0]?.nsn}; related parts are
          listed below it.
        </Alert>
      )}
      {onlySameTypeParts(identification.candidates) && (
        <Alert severity='info' data-testid='part-image-same-type-only'>
          Nothing in the photos identifies one exact part. These are parts of the same type, not matches.
        </Alert>
      )}
    </>
  );
};

const pendingLabelCards = (identification?: PartImageIdentification): PendingLabelCard[] => {
  if (!photosRead(identification)) return [{ key: 'reading' }];
  return identification.pendingLabels.map((label, index) => ({
    key: `${label.photoNumber}-${index}`,
    caption: `${label.kind} · photo ${label.photoNumber}`,
  }));
};

const EvidencePane = ({
  identification,
  narrowing,
}: {
  identification?: PartImageIdentification;
  narrowing?: ReactNode;
}) => {
  const labels = identification?.labels ?? [];
  return (
    <>
      {(!narrowing || labels.length > 0) && (
        <Box sx={{ mb: 2 }}>
          <LabelEvidence
            identificationId={identification?.id ?? ''}
            labels={labels}
            pending={pendingLabelCards(identification)}
          />
        </Box>
      )}
      {narrowing}
    </>
  );
};

interface CandidateListProperties {
  candidates: PartImageCandidate[];
  autoCreatedReportId?: string;
  isCreating: boolean;
  onCreateReport: (niin: string) => void;
  onOpenReport: (reportId: string) => void;
}

const CandidateAction = ({
  index,
  candidate,
  autoCreatedReportId,
  isCreating,
  onCreateReport,
  onOpenReport,
}: Omit<CandidateListProperties, 'candidates'> & { index: number; candidate: PartImageCandidate }) => {
  if (index === 0 && autoCreatedReportId) {
    return (
      <Button
        size='small'
        variant='contained'
        onClick={() => onOpenReport(autoCreatedReportId)}
        data-testid='part-image-open-report'
        sx={{ flexShrink: 0 }}
      >
        Open report
      </Button>
    );
  }
  return (
    <Button
      size='small'
      variant='outlined'
      disabled={isCreating}
      onClick={() => onCreateReport(candidate.niin)}
      data-testid='part-image-create-report'
      sx={{ flexShrink: 0 }}
    >
      Create report
    </Button>
  );
};

const CandidateList = ({ candidates, ...actionProperties }: CandidateListProperties) => (
  <Stack gap='8px'>
    {candidates.map((candidate, index) => (
      <CandidateRow key={candidate.niin} style={staggered(index)} data-testid='part-image-candidate'>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction='row' gap='8px' alignItems='center'>
            <Typography variant='subtitle2'>
              {index + 1}. {candidate.nsn} {candidate.itemName ?? ''}
            </Typography>
            <ImageMatchConfidencePill score={candidate.score} />
          </Stack>
          <Typography variant='caption' color='text.secondary' component='div'>
            {candidate.reasons.join(' · ')}
          </Typography>
        </Box>
        <CandidateAction index={index} candidate={candidate} {...actionProperties} />
      </CandidateRow>
    ))}
  </Stack>
);

const GroupedCandidateList = ({ candidates, ...actionProperties }: CandidateListProperties) => {
  const measured = candidates.filter(candidate => candidate.measured);
  const examples = candidates.filter(candidate => !candidate.measured);
  return (
    <Stack gap='12px'>
      {measured.length > 0 && (
        <>
          <SectionHeading testId='part-image-measured-heading'>Matches for your measurements</SectionHeading>
          <CandidateList candidates={measured} {...actionProperties} />
        </>
      )}
      {examples.length > 0 && (
        <>
          <SectionHeading testId='part-image-examples-heading'>Suggested from the photo</SectionHeading>
          <CandidateList candidates={examples} {...actionProperties} />
        </>
      )}
    </Stack>
  );
};

const CandidateListSkeleton = () => (
  <Stack gap='8px' data-testid='part-image-candidates-pending'>
    <SectionHeading>Suggested from the photo</SectionHeading>
    {Array.from({ length: SKELETON_CANDIDATE_ROWS }, (_, row) => (
      <CandidateRow key={row} style={staggered(row)}>
        <Box sx={{ flex: 1 }}>
          <Skeleton variant='text' width='55%' />
          <Skeleton variant='text' width='85%' height={16} />
        </Box>
        <Skeleton variant='rounded' width={104} height={30} />
      </CandidateRow>
    ))}
  </Stack>
);

const NoParts = ({ narrowable }: { narrowable: boolean }) => (
  <Stack gap='8px'>
    <SectionHeading>Suggested from the photo</SectionHeading>
    <Typography variant='body2' color='text.secondary' data-testid='part-image-no-parts-yet'>
      {narrowable
        ? 'Measure the part, and the FLIS parts that match are listed here.'
        : 'Nothing to suggest from these photos.'}
    </Typography>
  </Stack>
);

interface PartsPaneProperties extends Omit<CandidateListProperties, 'candidates' | 'autoCreatedReportId'> {
  identification?: PartImageIdentification;
  narrowable: boolean;
}

const PartsPane = ({ identification, narrowable, ...actionProperties }: PartsPaneProperties) => {
  if (!isComplete(identification)) return <CandidateListSkeleton />;
  if (identification.candidates.length === 0) return <NoParts narrowable={narrowable} />;
  const reportCreated = identification.outcome === PartImageIdentificationOutcome.ReportCreated;
  return (
    <GroupedCandidateList
      candidates={identification.candidates}
      autoCreatedReportId={reportCreated ? identification.reportId : undefined}
      {...actionProperties}
    />
  );
};

export interface PartImageIdentificationResultProperties {
  identification?: PartImageIdentification;
  isCreating: boolean;
  onCreateReport: (niin: string) => void;
  onOpenReport: (reportId: string) => void;
  narrowing?: ReactNode;
}

export const PartImageIdentificationResult = ({
  identification,
  narrowing,
  ...actionProperties
}: PartImageIdentificationResultProperties) => (
  <Stack gap='12px' sx={{ flex: { md: 1 }, minHeight: 0 }} data-testid='part-image-result'>
    <PhotoSummary identification={identification} />
    {isComplete(identification) && <OutcomeMessages identification={identification} />}
    <Panes data-testid='part-image-panes'>
      <Pane data-testid='part-image-evidence-pane'>
        <EvidencePane identification={identification} narrowing={narrowing} />
      </Pane>
      <Pane data-testid='part-image-parts-pane'>
        <PartsPane identification={identification} narrowable={Boolean(narrowing)} {...actionProperties} />
      </Pane>
    </Panes>
  </Stack>
);
